<template>
  <div class="graph-wrapper">
    <section v-if="isValid" class="graph-section" :class="{ 'graph-section--visible': isVisible }">
      <v-network-graph
          :configs="configs"
          :edges="graph.edges"
          :layouts="layouts"
          :nodes="graph.nodes"
          class="graph-canvas"
      />
    </section>
    <section v-else>
      <div class="sentence">{{ errorMessage }}</div>
    </section>
  </div>
</template>

<script setup>
import {onMounted, reactive, ref} from "vue";
import * as vNG from "v-network-graph";
import {VNetworkGraph} from "v-network-graph"; // eslint-disable-line no-unused-vars

const props = defineProps(['isValid', 'errorMessage', 'graph']);
const isVisible = ref(false);

function remapToVerticalTree(layoutNodes) {
  const LEVEL_SPACING = 120;
  const NODE_SPACING = 140;
  const byDepth = {};
  for (const [id, pos] of Object.entries(layoutNodes)) {
    if (!byDepth[pos.y]) byDepth[pos.y] = [];
    byDepth[pos.y].push({id, origX: pos.x});
  }
  const depths = Object.keys(byDepth).map(Number).sort((a, b) => a - b);
  const result = {};
  depths.forEach((depth, levelIdx) => {
    const siblings = byDepth[depth].sort((a, b) => b.origX - a.origX);
    const count = siblings.length;
    siblings.forEach((node, i) => {
      result[node.id] = {x: (i - (count - 1) / 2) * NODE_SPACING, y: levelIdx * LEVEL_SPACING};
    });
  });
  const ys = Object.values(result).map(p => p.y);
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
  for (const id of Object.keys(result)) result[id].y -= cy;
  return result;
}

const layouts = props.graph?.layouts?.nodes
  ? {nodes: remapToVerticalTree(props.graph.layouts.nodes)}
  : {nodes: {}};

class ReactiveLayout {
  activate({nodePositions, layouts: propLayouts, emitter}) {
    const nodes = propLayouts.value?.nodes;
    if (nodes) {
      for (const [id, pos] of Object.entries(nodes)) {
        if (!nodePositions.value[id]) nodePositions.value[id] = {x: 0, y: 0};
        nodePositions.value[id].x = pos.x;
        nodePositions.value[id].y = pos.y;
      }
    }
    this._onDrag = (positions) => {
      for (const [id, pos] of Object.entries(positions)) {
        if (!nodePositions.value[id]) nodePositions.value[id] = {x: 0, y: 0};
        nodePositions.value[id].x = pos.x;
        nodePositions.value[id].y = pos.y;
      }
    };
    emitter.on("node:dragstart", this._onDrag);
    emitter.on("node:pointermove", this._onDrag);
    emitter.on("node:dragend", this._onDrag);
    this._emitter = emitter;
  }
  deactivate() {
    this._emitter?.off("node:dragstart", this._onDrag);
    this._emitter?.off("node:pointermove", this._onDrag);
    this._emitter?.off("node:dragend", this._onDrag);
  }
}

onMounted(() => {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      isVisible.value = true;
    });
  });
});

const initialConfigs = vNG.defineConfigs({
  view: {
    layoutHandler: new ReactiveLayout(),
    autoPanAndZoomOnLoad: "fit-content",
    fitContentMargin: window.matchMedia('(max-height: 639px) and (orientation: landscape)').matches ? "30%" : "10%",
  },
  node: {
    label: {
      visible: true,
      fontFamily: "'TASA Orbiter', sans-serif",
      fontSize: 17,
      lineHeight: 1.2,
      color: "#ffffff",
      margin: 12,
      direction: "south",
      text: "name",
    },
    normal: {type: "circle", radius: 14, strokeWidth: 0, strokeColor: "#3a3a3a", color: "#3a3a3a"},
    hover: {color: "#666666", strokeWidth: 0, strokeColor: "#666666"},
  },
  edge: {
    normal: {width: 2, color: "#3a3a3a", dasharray: "0", linecap: "round", animate: false},
    hover: {color: "#666666", width: 2},
  },
});
const configs = reactive(initialConfigs);
</script>

<style scoped>
.graph-wrapper { width: 100%; }

.graph-section {
  width: 100%;
  transform: scale(0.05);
  opacity: 0;
  transform-origin: 50% 50%;
  transition: transform 800ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 400ms ease-out;
}

.graph-section--visible {
  transform: scale(1);
  opacity: 1;
}

.graph-canvas {
  width: 100%;
  height: calc(100dvh - 268px);
}

@media screen and (max-height: 639px) and (orientation: landscape) {
  .graph-canvas {
    height: 100dvh;
  }
}
</style>
